import * as THREE from 'three';
import type { EnemyModel, EnemyType } from '../assets/modelFactories/EnemyFactory';
import type { Tower } from './Tower';

export type EnemyStats = {
  type: EnemyType;
  movement: 'ground' | 'air' | 'paratrooper';
  maxHealth: number;
  speed: number;
  reward: number;
  damageToObjective: number;
  attackRange: number;
  attackDamage: number;
  attackRate: number;
};

export const ENEMY_STATS: Record<EnemyType, EnemyStats> = {
  // 步枪兵：射程中等，行进间射击
  infantry: { type: 'infantry', movement: 'ground', maxHealth: 35, speed: 1.8, reward: 8, damageToObjective: 1, attackRange: 2.6, attackDamage: 4, attackRate: 1.1 },
  // 坦克：远程重炮，移动射击时最慢
  tank: { type: 'tank', movement: 'ground', maxHealth: 220, speed: 0.9, reward: 28, damageToObjective: 3, attackRange: 5.2, attackDamage: 20, attackRate: 0.5 },
  // 登陆艇：车载机枪，射程比步兵远
  landingcraft: { type: 'landingcraft', movement: 'ground', maxHealth: 150, speed: 1.1, reward: 18, damageToObjective: 2, attackRange: 3.6, attackDamage: 6, attackRate: 1.2 },
  // 飞机：不攻击塔，直接冲向目标
  plane: { type: 'plane', movement: 'air', maxHealth: 30, speed: 4.2, reward: 10, damageToObjective: 2, attackRange: 0, attackDamage: 0, attackRate: 0 },
  // 伞兵：冲锋枪近程高射速
  paratrooper: { type: 'paratrooper', movement: 'paratrooper', maxHealth: 28, speed: 1.7, reward: 7, damageToObjective: 1, attackRange: 2.0, attackDamage: 3, attackRate: 1.6 },
};

export type EnemySpawnConfig = {
  path?: THREE.CatmullRomCurve3;
  start?: THREE.Vector3;
  target?: THREE.Vector3;
};

export type EnemyAttackEvent = {
  position: THREE.Vector3;
  target: Tower;
  damage: number;
};

export class Enemy {
  readonly group: THREE.Group;
  readonly stats: EnemyStats;
  readonly radius: number;
  health: number;
  active = true;
  reachedObjective = false;

  pathIndex = 0;
  pathProgress = 0;
  attackCooldown = 0;

  private readonly walkOffset = Math.random() * 100;
  private readonly temp = new THREE.Vector3();
  private readonly velocity = new THREE.Vector3();
  private readonly startPosition = new THREE.Vector3();
  private readonly targetPosition = new THREE.Vector3();

  constructor(
    type: EnemyType,
    model: EnemyModel,
    private readonly config: EnemySpawnConfig,
  ) {
    this.stats = ENEMY_STATS[type];
    this.health = this.stats.maxHealth;
    this.radius = model.radius;
    this.group = model.root;
    this.group.userData.enemy = this;

    if (this.stats.movement === 'ground') {
      if (!config.path) throw new Error(`Ground enemy ${type} requires a path`);
      this.group.position.copy(config.path.getPointAt(0));
    } else {
      if (!config.start || !config.target) throw new Error(`${type} requires start and target`);
      this.startPosition.copy(config.start);
      this.targetPosition.copy(config.target);
      this.group.position.copy(this.startPosition);
      this.velocity.subVectors(this.targetPosition, this.startPosition).normalize().multiplyScalar(this.stats.speed);
    }
  }

  update(delta: number, elapsed: number, targetTower?: Tower | null): EnemyAttackEvent | null {
    if (!this.active || this.reachedObjective) return null;

    this.attackCooldown = Math.max(0, this.attackCooldown - delta);

    const canAttack =
      targetTower && targetTower.active && this.stats.attackRange > 0 && this.isWithinRange(targetTower);
    const attackMoveScale = canAttack ? this.getAttackMoveScale() : 1;

    switch (this.stats.movement) {
      case 'ground':
        this.updateGround(delta, elapsed, attackMoveScale);
        break;
      case 'air':
        this.updateAir(delta, elapsed, attackMoveScale);
        break;
      case 'paratrooper':
        this.updateParatrooper(delta, elapsed, attackMoveScale);
        break;
    }

    if (canAttack) {
      return this.updateAttack(delta, targetTower!);
    }

    return null;
  }

  private getAttackMoveScale(): number {
    switch (this.stats.type) {
      case 'tank':
        return 0.35;
      case 'landingcraft':
        return 0.55;
      case 'infantry':
        return 0.65;
      case 'paratrooper':
        return 0.7;
      default:
        return 0.5;
    }
  }

  private updateGround(delta: number, elapsed: number, speedScale = 1): void {
    const path = this.config.path!;
    const totalLength = path.getLength();

    const speedJitter = this.stats.type === 'infantry' ? 0.2 : this.stats.type === 'tank' ? 0 : 0.08;
    const speedMod = 1 + Math.sin((elapsed + this.walkOffset) * 3) * speedJitter;
    const moveAmount = (this.stats.speed * speedMod * speedScale * delta) / totalLength;
    this.pathProgress += moveAmount;

    if (this.pathProgress >= 1) {
      this.pathProgress = 1;
      this.onReachedObjective();
      return;
    }

    path.getPointAt(this.pathProgress, this.group.position);

    const lookAhead = Math.min(this.pathProgress + 0.02, 1);
    path.getPointAt(lookAhead, this.temp);
    this.temp.sub(this.group.position);
    if (this.temp.lengthSq() > 0.0001) {
      const angle = Math.atan2(this.temp.x, this.temp.z);
      this.group.rotation.y = angle;

      const tangent = this.temp.clone().normalize();
      const right = new THREE.Vector3(tangent.z, 0, -tangent.x);
      const wanderAmp = this.stats.type === 'infantry' ? 0.55 : this.stats.type === 'tank' ? 0.18 : 0.3;
      const wanderFreq = this.stats.type === 'infantry' ? 3 : this.stats.type === 'tank' ? 1 : 2;
      const lateral = Math.sin(this.pathProgress * wanderFreq * Math.PI * 2 + this.walkOffset) * wanderAmp;
      this.group.position.addScaledVector(right, lateral);
    }

    if (this.stats.type === 'infantry') {
      this.group.position.y = Math.abs(Math.sin((elapsed + this.walkOffset) * 8)) * 0.08;
    } else if (this.stats.type === 'tank') {
      this.group.position.y = 0;
    } else {
      this.group.position.y = Math.sin(elapsed * 2 + this.walkOffset) * 0.04;
    }
  }

  private updateAir(delta: number, elapsed: number, speedScale = 1): void {
    this.group.position.addScaledVector(this.velocity, delta * speedScale);
    this.group.position.y = Math.max(this.group.position.y, 3);

    const side = new THREE.Vector3(this.velocity.z, 0, -this.velocity.x).normalize();
    const drift = Math.sin(elapsed * 2 + this.walkOffset) * 1.4 * delta;
    this.group.position.addScaledVector(side, drift);

    const angle = Math.atan2(this.velocity.x, this.velocity.z);
    this.group.rotation.y = angle;
    this.group.rotation.z = Math.sin(elapsed * 3 + this.walkOffset) * 0.04;

    if (this.group.position.distanceToSquared(this.targetPosition) < 0.25) {
      this.onReachedObjective();
    }
  }

  private updateParatrooper(delta: number, elapsed: number, speedScale = 1): void {
    const dir = this.temp.subVectors(this.targetPosition, this.group.position);
    const dist = dir.length();
    if (dist < 0.25) {
      this.onReachedObjective();
      return;
    }
    dir.normalize();

    const side = new THREE.Vector3(dir.z, 0, -dir.x);
    const drift = Math.sin(elapsed * 1.5 + this.walkOffset) * 0.9 * delta;
    this.group.position.addScaledVector(dir, this.stats.speed * speedScale * delta);
    this.group.position.addScaledVector(side, drift);
    this.group.position.y = Math.max(0, this.group.position.y);

    const faceDir = this.temp.subVectors(this.targetPosition, this.group.position).normalize();
    this.group.rotation.y = Math.atan2(faceDir.x, faceDir.z);
    this.group.position.y += Math.sin((elapsed + this.walkOffset) * 6) * 0.015;
  }

  private isWithinRange(tower: Tower): boolean {
    const dx = this.group.position.x - tower.group.position.x;
    const dz = this.group.position.z - tower.group.position.z;
    return dx * dx + dz * dz <= this.stats.attackRange * this.stats.attackRange;
  }

  private updateAttack(_delta: number, tower: Tower): EnemyAttackEvent | null {
    this.facePosition(tower.group.position);

    if (this.attackCooldown <= 0) {
      this.attackCooldown = 1 / this.stats.attackRate;
      tower.takeDamage(this.stats.attackDamage);
      return { position: this.group.position.clone(), target: tower, damage: this.stats.attackDamage };
    }

    return null;
  }

  private facePosition(position: THREE.Vector3): void {
    this.temp.subVectors(position, this.group.position);
    this.temp.y = 0;
    if (this.temp.lengthSq() > 0.0001) {
      this.group.rotation.y = Math.atan2(this.temp.x, this.temp.z);
    }
  }

  private onReachedObjective(): void {
    this.reachedObjective = true;
    this.active = false;
    this.group.visible = false;
  }

  takeDamage(amount: number): void {
    this.health -= amount;
    if (this.health <= 0) {
      this.active = false;
      this.group.visible = false;
    }
  }

  dispose(): void {
    this.group.clear();
  }
}
